import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fmtBRL, fmtDate, statusLabel, statusColor, monthShort } from '@/lib/format';
import { DASH } from '@/constants/testIds';
import { Card } from '@/components/ui/card';
import {
  Wallet, TrendingUp, TrendingDown, ArrowDownLeft, ArrowUpRight, AlertTriangle,
  ArrowUpRight as ArrowNE,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Link } from 'react-router-dom';

const COLORS = ['#10B981', '#2563EB', '#F59E0B', '#F43F5E', '#06B6D4', '#8B5CF6', '#EC4899', '#84CC16'];

function KpiCard({ label, value, sub, icon: Icon, tone = 'default', testid }) {
  const toneMap = {
    default: 'text-foreground',
    good: 'text-emerald-500 dark:text-emerald-400',
    bad: 'text-rose-500 dark:text-rose-400',
    warn: 'text-amber-500 dark:text-amber-400',
    info: 'text-blue-500 dark:text-blue-400',
  };
  return (
    <Card data-testid={testid} className="card-elevate p-5 border-border/70">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{label}</div>
          <div className={`mt-2 text-2xl font-heading font-bold tabular-nums ${toneMap[tone]}`}>{fmtBRL(value)}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className={`h-10 w-10 grid place-items-center rounded-lg ${
          tone === 'good' ? 'bg-emerald-500/10' : tone === 'bad' ? 'bg-rose-500/10' :
          tone === 'warn' ? 'bg-amber-500/10' : tone === 'info' ? 'bg-blue-500/10' : 'bg-muted'
        }`}>
          <Icon className={`h-5 w-5 ${toneMap[tone]}`} />
        </div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get('/dashboard')).data,
  });

  const { data: cats } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get('/categories')).data,
  });
  const catMap = (cats || []).reduce((a, c) => (a[c.id] = c, a), {});

  if (isLoading || !data) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="h-28 rounded-lg border bg-card/40 animate-pulse" />))}
    </div>;
  }

  const cashflow = data.cashflow.map(c => ({ ...c, label: monthShort(c.month) }));
  const catData = data.expense_by_category.slice(0, 8);

  return (
    <div data-testid={DASH.container} className="space-y-6">
      <div className="flex flex-col gap-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Visão geral</div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Dashboard</h1>
      </div>

      {/* KPI ROW */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-6">
        <KpiCard testid={DASH.kpiBalance} label="Saldo Atual" value={data.kpis.balance} icon={Wallet} tone="info" />
        <KpiCard testid={DASH.kpiIncome} label="Entradas do Mês" value={data.kpis.income_month} icon={TrendingUp} tone="good" />
        <KpiCard testid={DASH.kpiExpense} label="Saídas do Mês" value={data.kpis.expense_month} icon={TrendingDown} tone="bad" />
        <KpiCard testid={DASH.kpiReceivable} label="A Receber" value={data.kpis.receivable} icon={ArrowDownLeft} tone="good" />
        <KpiCard testid={DASH.kpiPayable} label="A Pagar" value={data.kpis.payable} icon={ArrowUpRight} tone="warn" />
        <KpiCard testid={DASH.kpiOverdue} label="Vencidas" value={data.kpis.overdue} icon={AlertTriangle} tone="bad" />
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card data-testid={DASH.chartCashflow} className="lg:col-span-2 p-5 border-border/70">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">12 meses</div>
              <h3 className="font-heading text-lg font-semibold">Fluxo de Caixa</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cashflow} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v) => fmtBRL(v)}
                />
                <Bar dataKey="income" name="Entradas" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Saídas" fill="#F43F5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card data-testid={DASH.chartCategory} className="p-5 border-border/70">
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Este mês</div>
            <h3 className="font-heading text-lg font-semibold">Despesas por Categoria</h3>
          </div>
          {catData.length === 0 ? (
            <div className="h-56 grid place-items-center text-sm text-muted-foreground">Sem dados</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={catData} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
            {catData.map((c, i) => (
              <div key={c.category_id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                  <span className="truncate">{c.name}</span>
                </div>
                <span className="font-mono tabular-nums text-muted-foreground shrink-0 ml-2">{fmtBRL(c.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* LOWER GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid={DASH.listUpcoming} className="p-5 border-border/70">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-semibold">Próximos Vencimentos</h3>
            <Link to="/payable" className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1">Ver todos <ArrowNE className="h-3 w-3" /></Link>
          </div>
          <div className="divide-y divide-border/70">
            {data.upcoming.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Nada nos próximos 15 dias.</div>}
            {data.upcoming.map((t) => (
              <div key={t.id} className="py-2.5 flex items-center gap-3">
                <div className={`h-9 w-9 rounded-lg grid place-items-center ${t.type === 'INCOME' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                  {t.type === 'INCOME' ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.description}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(t.due_date)} · <span className={`chip ${statusColor[t.status]}`}>{statusLabel[t.status]}</span></div>
                </div>
                <div className={`font-mono tabular-nums text-sm font-semibold ${t.type === 'INCOME' ? 'money-in' : 'money-out'}`}>
                  {t.type === 'INCOME' ? '+' : '-'}{fmtBRL(t.amount)}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card data-testid={DASH.listRecent} className="p-5 border-border/70">
          <h3 className="font-heading text-lg font-semibold mb-3">Últimas Movimentações</h3>
          <div className="divide-y divide-border/70">
            {data.recent.map((t) => (
              <div key={t.id} className="py-2.5 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg grid place-items-center bg-muted/50">
                  <span className="text-[10px] uppercase font-mono text-muted-foreground">{catMap[t.category_id]?.name?.slice(0, 3) || '—'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{t.description}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(t.due_date)}</div>
                </div>
                <div className={`font-mono tabular-nums text-sm font-semibold ${t.type === 'INCOME' ? 'money-in' : 'money-out'}`}>
                  {t.type === 'INCOME' ? '+' : '-'}{fmtBRL(t.amount)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ACCOUNTS RECAP */}
      <Card className="p-5 border-border/70">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Suas Contas</h3>
          <Link to="/accounts" className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline">Gerenciar</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.accounts.map((a) => (
            <div key={a.id} className="rounded-lg border p-3 hover:border-emerald-500/40 transition-colors">
              <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{a.type}</div>
              <div className="font-medium truncate">{a.name}</div>
              <div className="mt-2 font-mono tabular-nums text-lg font-bold">{fmtBRL(a.current_balance)}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
